import * as Long from "long";
import { selectBalancingXPBuffFactor } from "../balancingSelectors/selectBalancingBuffs";
import { selectBalancingCoopUnlockLevel } from "../balancingSelectors/selectBalancingConstants";
import { selectCoopProjects, selectCoopProjectType, } from "../balancingSelectors/selectBalancingCoopProjects";
import { selectCoopLanguageSettings, selectRankingFilterLanguageSettings, } from "../balancingSelectors/selectBalancingLanguage";
import { selectCoopLevels } from "../balancingSelectors/selectBalancingXpLevels";
import { CoopConstants } from "../constants/CoopConstants";
import { IdLeagues } from "../constants/IdLeagues";
import { IdCoopCategory } from "../features/cooperative/IdCoopCategory";
import { IdCoopMemberActivityClassName } from "../features/cooperative/info-view/IdCoopMemberActivityClassName";
import { IdCoopRole } from "../features/cooperative/info-view/IdCoopRole";
import { mapCoopInvitePlayerError } from "../features/cooperative/mapCoopInvitePlayerError";
import { IdModal } from "../features/modals/IdModal";
import { IdLoca } from "../utils/i18n/IdLoca";
import { utilCoopProject } from "../utils/utilCoopProject";
import { utilNumber } from "../utils/utilNumber";
import { utilTime } from "../utils/utilTime";
import { selectRunningCoopProjectEventInfo } from "./selectEventInfo";
import { selectModalTabId } from "./selectModalTabId";
import { selectPlayerInfo } from "./selectPlayerInfo";
import { selectPlayerLevel } from "./selectPlayerLevelInfo";
var coopParamToCoopProfileInfo = function (coop) {
    var id = coop.id, _a = coop.xp, xp = _a === void 0 ? Long.ZERO : _a, name = coop.name, _b = coop.description, description = _b === void 0 ? "" : _b, language = coop.language;
    if (!id || xp === undefined || !name || !language) {
        return undefined;
    }
    return {
        coopLeagueId: IdLeagues.Bronze1,
        description: description,
        id: id,
        level: selectCoopLevelFromXp(Long.fromValue(xp)),
        name: name,
    };
};
export var selectCoopInfoView = function (state) {
    var type = selectModalTabId(state, IdModal.Coop);
    return type === IdCoopCategory.CoopInfoView
        ? state.coopInfo.coop
        : state.coopInfo.search.selectedCoop.coop;
};
export var selectCoopInfoViewId = function (state) {
    var coop = selectCoopInfoView(state);
    return coop ? coop.id : Long.NEG_ONE;
};
export var selectCoopInfoViewMember = function (state) {
    var type = selectModalTabId(state, IdModal.Coop);
    return type === IdCoopCategory.CoopInfoView
        ? state.coopInfo.member || []
        : state.coopInfo.search.selectedCoop.member || [];
};
export var selectCoop = function (state) { return state.coopInfo.coop; };
export var selectCoopInfo = function (state) {
    if (!state.coopInfo.coop) {
        return undefined;
    }
    return coopParamToCoopProfileInfo(state.coopInfo.coop);
};
export var selectCoopXp = function (state) {
    var coop = state.coopInfo.coop;
    return coop && coop.xp ? coop.xp : Long.ZERO;
};
export var selectCurrentCoopLevel = function (state) {
    var coopXp = selectCoopXp(state);
    return selectCoopLevelFromXp(coopXp);
};
export var selectCoopLevel = function (state) { return selectCoopLevelFromXp(selectCoopXp(state)); };
export var selectCoopLevelFromXp = function (xp) {
    if (xp.lessThanOrEqual(Long.ZERO)) {
        return 1;
    }
    var levels = selectCoopLevels();
    var levelsDescending = Array.from(levels.values()).reverse();
    var highestAvailableLevel = levelsDescending.find(function (level) {
        return xp.greaterThanOrEqual(level.requiredCoopXp);
    });
    return highestAvailableLevel ? highestAvailableLevel.level : -1;
};
export var selectIsCoopAvailable = function (state) {
    var playerLevel = selectPlayerLevel(state);
    var requiredLevel = selectBalancingCoopUnlockLevel();
    return playerLevel >= requiredLevel;
};
export var selectJoinableCoops = function (state) { return state.coopInfo.search.coops; };
export var selectCoopFoundLanguage = function (state) {
    var languages = selectRankingFilterLanguageSettings();
    var selectedLanguage = state.coopInfo.found.selectedLanguage;
    if (selectedLanguage) {
        return languages.find(function (lang) { return lang.languageCode === selectedLanguage.languageCode; });
    }
    var currentCoopLanguageCode = getCurrentCoopLanguageCode(state);
    var currentLanguage = languages.find(function (language) { return language.languageCode === currentCoopLanguageCode; });
    if (currentLanguage) {
        return currentLanguage;
    }
    else {
        return languages[0];
    }
};
var getCurrentCoopLanguageCode = function (state) {
    if (state.coopInfo.coop && state.coopInfo.coop.language) {
        return state.coopInfo.coop.language;
    }
    return state.loca.lang;
};
export var selectCoopSelectedLanguage = function (state) {
    var language = selectCoopLanguageSettings();
    var currentCoopTab = selectModalTabId(state, IdModal.Coop);
    var selectedLanguage = currentCoopTab === IdCoopCategory.CoopSearch
        ? state.coopInfo.search.selectedLanguage
        : state.coopInfo.found.selectedLanguage;
    if (selectedLanguage) {
        return (language.find(function (lang) { return lang.languageCode === selectedLanguage.languageCode; }) || language[0]);
    }
    return language[0];
};
export var selectCoopIsSearchNotFound = function (state) {
    var _a = selectCoopSearch(state), coop = _a.coop, member = _a.member;
    return coop === undefined && member !== undefined && member.length === 0;
};
export var selectCoopSearch = function (state) {
    var selectedCoop = state.coopInfo.search.selectedCoop;
    if (selectedCoop == undefined) {
        return;
    }
    return selectedCoop;
};
export var selectActiveInvite = function (state) {
    return state.coopInfo.invite ? state.coopInfo.invite.activeInvite : undefined;
};
export var selectHasActiveInvite = function (state) {
    return selectActiveInvite(state) !== undefined;
};
export var selectInvitingCoopInfo = function (state) {
    if (!state.coopInfo.invite || !state.coopInfo.invite.invitingCoop) {
        return undefined;
    }
    return coopParamToCoopProfileInfo(state.coopInfo.invite.invitingCoop);
};
export var selectCoopMemberLastLoginClass = function (lastLogin) {
    var classNamePrefix = IdCoopMemberActivityClassName.prefix;
    if (!lastLogin) {
        return classNamePrefix + IdCoopMemberActivityClassName.other;
    }
    var oneMinute = 60;
    var oneDay = 60 * 60 * 24;
    if (lastLogin <= oneMinute * 20) {
        return classNamePrefix + IdCoopMemberActivityClassName.last20min;
    }
    else if (lastLogin <= oneDay) {
        return classNamePrefix + IdCoopMemberActivityClassName.last24hours;
    }
    else if (lastLogin <= oneDay * 2) {
        return classNamePrefix + IdCoopMemberActivityClassName.last48hours;
    }
    else if (lastLogin <= oneDay * 7) {
        return classNamePrefix + IdCoopMemberActivityClassName.last7days;
    }
    else {
        return classNamePrefix + IdCoopMemberActivityClassName.other;
    }
};
export var selectCoopSearchSelectedCoop = function (state) {
    return state.coopInfo.search.selectedCoop.coop;
};
export var selectCoopPointsToCollect = function (state) {
    return state.coopInfo.help.points;
};
export var selectCoopSendLoyaltyCooldownEndTime = function (state) {
    return state.coopInfo.help.cooldownEndTime;
};
export var selectCoopProjectPoints = function (state) {
    return state.coopInfo.help.projectPoints;
};
export var selectBuffedXpFromProjectInfo = function (state, projectTypeId, difficulty) {
    var projects = selectCoopProjects();
    var foundProject = projects.find(function (proj) { return proj.id === projectTypeId; });
    var projectHasSelectedDifficulty = foundProject != undefined && foundProject.difficultyLevel[difficulty];
    var baseAmount = projectHasSelectedDifficulty
        ? Long.fromValue(foundProject.difficultyLevel[difficulty].coopXpReward)
        : Long.ZERO;
    if (Long.ZERO.equals(baseAmount)) {
        return baseAmount;
    }
    var event = selectRunningCoopProjectEventInfo(state);
    var proto = event ? event.proto : undefined;
    var rewardId = proto && proto.rewardId ? proto.rewardId : undefined;
    if (rewardId) {
        var buffFactor = selectCoopXpBuffFactorForProjectType(state, foundProject);
        if (buffFactor != undefined) {
            return utilNumber.multiplyRounding(baseAmount, buffFactor);
        }
    }
    return baseAmount;
};
export var selectCoopShowNotification = function (state) {
    var isCoopAvailable = selectIsCoopAvailable(state);
    var playerCoop = selectCoop(state);
    if (!isCoopAvailable || !playerCoop) {
        return false;
    }
    var hasProjectPoints = selectCoopProjectPoints(state) != undefined;
    var hasMemberPoints = selectCoopPointsToCollect(state).length > 0;
    var canSendLoyalty = selectCoopSendLoyaltyCooldownEndTime(state) === undefined;
    return hasProjectPoints || hasMemberPoints || canSendLoyalty;
};
export var selectCoopMembers = function (state) { return state.coopInfo.member || []; };
export var selectCoopMemberByPlayerId = function (state, id) {
    return selectCoopMembers(state).find(function (member) { return member.playerId.equals(id); });
};
export var selectCoopIsMemberSameCoop = function (state, memberId) {
    var members = selectCoopMembers(state);
    if (members.length === 0) {
        return false;
    }
    return members.some(function (m) { return m.playerId.equals(memberId); });
};
export var selectCoopRole = function (state, playerId) {
    var members = selectCoopMembers(state);
    if (!playerId) {
        playerId = selectPlayerInfo(state).playerId;
    }
    var self = members.find(function (m) { return m.playerId.equals(playerId); });
    return self ? self.role || IdCoopRole.Leader : undefined;
};
export var selectCoopCanKick = function (state, playerIdToKick) {
    var sameCoop = selectCoopIsMemberSameCoop(state, playerIdToKick);
    var toKickedRole = selectCoopRole(state, playerIdToKick);
    var kickerRole = selectCoopRole(state);
    return (sameCoop &&
        toKickedRole !== IdCoopRole.Leader &&
        (kickerRole === IdCoopRole.Leader || kickerRole === IdCoopRole.Deputy));
};
export var selectCoopCanPromoteDemote = function (state, toPromoteDemoteplayerId) {
    var isMemberInCoop = selectCoopIsMemberSameCoop(state, toPromoteDemoteplayerId);
    if (!isMemberInCoop) {
        return false;
    }
    var myRole = selectCoopRole(state);
    if (myRole === IdCoopRole.Member) {
        return false;
    }
    var toPromoteDemotePlayerRole = selectCoopRole(state, toPromoteDemoteplayerId);
    return toPromoteDemotePlayerRole !== IdCoopRole.Leader;
};
export var selectCoopCanPromoteDemoteSelf = function (state) {
    var role = selectCoopRole(state);
    return role === IdCoopRole.Leader || role === IdCoopRole.Deputy;
};
export var selectCoopMemberIsInProbation = function (state, playerId) {
    var members = selectCoopMembers(state);
    var memberToCheck = members.find(function (m) { return m.playerId.equals(playerId); }) || {};
    var joinTime = memberToCheck.joinTime;
    return joinTime ? joinTime < CoopConstants.ProbationPeriodInSeconds : true;
};
export var selectCoopNoticeEvents = function (state) {
    var eventNotices = state.coopInfo.eventNotices;
    eventNotices.sort(function (a, b) {
        return (b.creationTime || 0) - (a.creationTime || 0);
    });
    return eventNotices;
};
export var selectCoopName = function (state) {
    return state.coopInfo.coop ? state.coopInfo.coop.name : undefined;
};
export var selectCoopMessage = function (state) {
    return state.coopInfo.coop ? state.coopInfo.coop.internalNotice : undefined;
};
export var selectCoopActiveProject = function (state) {
    var project = state.coopInfo.project;
    return project && !project.completed ? project : undefined;
};
export var selectCoopCompletedProject = function (state) {
    var project = state.coopInfo.project;
    return project && project.completed ? project : undefined;
};
export var selectCoopActiveProjectExpired = function (state) {
    return (state.coopInfo.project != undefined &&
        utilTime.secondsLeftTill(selectCoopActiveProjectEndTime(state)) <= 0);
};
export var selectCoopActiveProjectEndTime = function (state) {
    return state.coopInfo.projectEndTime;
};
export var selectCoopSelectedProjectTypeId = function (state) {
    return state.coopInfo.selectedProjectTypeId;
};
export var selectCoopSelectedProjectType = function (state) {
    var selectedProjectTypeId = selectCoopSelectedProjectTypeId(state);
    return selectCoopProjectType(selectedProjectTypeId);
};
export var selectCoopProjectTypeInfo = function (state) {
    return state.coopInfo.projectTypeInfo;
};
export var selectCoopSelectedProjectDifficultyLevel = function (state) {
    return state.coopInfo.selectedProjectDifficulty || 1;
};
export var selectCoopMemberProjectInfo = function (state) {
    return state.coopInfo.contributionInfo;
};
export var selectCoopProjectRankingInformation = function (state) {
    var projects = selectCoopProjects();
    var activeCoopProject = selectCoopActiveProject(state);
    if (activeCoopProject !== undefined &&
        activeCoopProject.difficulty !== undefined &&
        activeCoopProject.typeId !== undefined &&
        projects[activeCoopProject.typeId] !== undefined) {
        return (projects[activeCoopProject.typeId].difficultyLevel[activeCoopProject.difficulty - 1]
            .rankingRewards || []);
    }
    return [];
};
export var selectCoopCanInvite = function (state, playerIdToInvite) {
    var role = selectCoopRole(state);
    return ((role === undefined || (role === IdCoopRole.Leader || role === IdCoopRole.Deputy)) &&
        selectCoop(state) !== undefined &&
        !selectHasInvitedPlayer(state, playerIdToInvite) &&
        !selectCoopIsMemberSameCoop(state, playerIdToInvite));
};
export var selectHasInvitedPlayer = function (state, playerIdToInvite) {
    var inviteResponse = state.coopInfo.invite.inviteResponse;
    return (inviteResponse !== undefined &&
        inviteResponse.length > 0 &&
        inviteResponse.some(function (invite) { return invite.playerId !== undefined && invite.playerId.equals(playerIdToInvite); }));
};
export var selectCoopInviteResult = function (state, playerIdToInvite) {
    var coopInviteErrors = state.errors.CoopInvitePlayerErrors;
    if (coopInviteErrors.length > 0) {
        var errorCode = coopInviteErrors[coopInviteErrors.length - 1];
        return mapCoopInvitePlayerError.get(errorCode);
    }
    else if (playerIdToInvite !== undefined && selectHasInvitedPlayer(state, playerIdToInvite)) {
        return IdLoca.floatingText_coopInviteSuccessful;
    }
    return undefined;
};
export var selectCoopMenuFirstTab = function (state) {
    return state.coopInfo.firstTab;
};
export var selectCoopXpBuffFactorForProjectType = function (state, projectType) {
    var buffFactor = 1;
    var runningCoopProjectEvent = selectRunningCoopProjectEventInfo(state);
    if (runningCoopProjectEvent) {
        var proto = runningCoopProjectEvent.proto;
        if (projectType.id === proto.rewardId) {
            buffFactor = selectBalancingXPBuffFactor(projectType.buffId);
        }
    }
    return buffFactor;
};
export var selectCoopProjectDifficulty = function (state, projectType) {
    var selectedCoopProjectDifficultyLevel = selectCoopSelectedProjectDifficultyLevel(state);
    return utilCoopProject.getProjectDifficultyLevelType(projectType, selectedCoopProjectDifficultyLevel);
};



//////////////////
// WEBPACK FOOTER
// ./src/selectors/selectCoopInfo.ts
// module id = 30
// module chunks = 3